import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { SimliClient, LogLevel } from "simli-client/dist/client";
import { api } from "@/lib/apiClient";

// ---- decode an mp3 URL -> mono PCM16 @16kHz for Simli ----
function pcm16FromAudioBuffer(buffer) {
  const channels = buffer.numberOfChannels, length = buffer.length;
  const out = new Int16Array(length);
  for (let i = 0; i < length; i++) {
    let s = 0;
    for (let c = 0; c < channels; c++) s += buffer.getChannelData(c)[i];
    s = Math.max(-1, Math.min(1, s / channels));
    out[i] = s < 0 ? s * 32768 : s * 32767;
  }
  return out;
}
function resampleTo16k(input, rate) {
  if (rate === 16000) return input;
  const outLen = Math.round((input.length * 16000) / rate);
  const out = new Int16Array(outLen);
  const ratio = rate / 16000;
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio, l = Math.floor(pos), r = Math.min(l + 1, input.length - 1), f = pos - l;
    out[i] = Math.round(input[l] * (1 - f) + input[r] * f);
  }
  return out;
}
async function mp3UrlToPcm16(url) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`tts fetch ${res.status}`);
  const bytes = await res.arrayBuffer();
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const decoded = await ctx.decodeAudioData(bytes);
    return resampleTo16k(pcm16FromAudioBuffer(decoded), decoded.sampleRate);
  } finally { try { await ctx.close(); } catch {} }
}
function sendInChunks(client, pcm) {
  const SAMPLES = 3000;
  for (let o = 0; o < pcm.length; o += SAMPLES) {
    const c = pcm.slice(o, Math.min(o + SAMPLES, pcm.length));
    client.sendAudioData(new Uint8Array(c.buffer, c.byteOffset, c.byteLength));
  }
}

// Reusable live coach avatar. Imperative API via ref:
//   await ref.start() -> bool (true if the live avatar connected)
//   ref.isReady()
//   await ref.speakUrl(fullMp3Url) -> durationMs (throws if not ready)
//   ref.clear() / ref.stop()
const SimliAvatar = forwardRef(function SimliAvatar({ size = 210, fallback = null }, ref) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const clientRef = useRef(null);
  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);

  useImperativeHandle(ref, () => ({
    async start() {
      if (clientRef.current) return readyRef.current;
      try {
        const { data: av } = await api.get("/simli/available");
        if (!av.available) return false;
        if (!videoRef.current || !audioRef.current) return false; // element not mounted yet
        const { data: sess } = await api.post("/simli/session");
        const client = new SimliClient(sess.session_token, videoRef.current, audioRef.current, null, LogLevel?.INFO, "livekit");
        client.on("error", (e) => console.warn("[simli]", e));
        client.on("startup_error", (e) => console.warn("[simli startup]", e));
        await client.start();
        clientRef.current = client;
        readyRef.current = true;
        setReady(true);
        return true;
      } catch (e) {
        console.warn("[simli] unavailable, audio-only:", e?.message || e);
        return false;
      }
    },
    isReady() { return readyRef.current; },
    async speakUrl(url) {
      if (!clientRef.current) throw new Error("avatar not ready");
      const pcm = await mp3UrlToPcm16(url);
      try { clientRef.current.ClearBuffer(); } catch {}
      sendInChunks(clientRef.current, pcm);
      return (pcm.length / 16000) * 1000;
    },
    clear() { try { clientRef.current?.ClearBuffer(); } catch {} },
    stop() {
      try { clientRef.current?.ClearBuffer(); } catch {}
      try { clientRef.current?.stop(); } catch {}
      clientRef.current = null;
      readyRef.current = false;
      setReady(false);
    },
  }), []);

  return (
    <div className="rounded-full overflow-hidden flex items-center justify-center bg-muted/30" style={{ width: size, height: size }}>
      <video ref={videoRef} autoPlay playsInline muted className={ready ? "w-full h-full object-cover" : "hidden"} data-testid="simli-video" />
      {!ready && fallback}
      <audio ref={audioRef} autoPlay playsInline className="hidden" />
    </div>
  );
});

export default SimliAvatar;
